import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { orderAPI } from '../../services/marketplaceAPI';
import { getEmergencyServiceImageUrl } from '../../utils/imageUtils';

const OrderDetailPage = () => {
  const { orderId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [cancelling, setCancelling] = useState(false);

  // Check if order was just created
  const orderCreated = location.state?.orderCreated;

  useEffect(() => {
    fetchOrder();
  }, [orderId]);

  const fetchOrder = async () => {
    try {
      setLoading(true);
      setError('');
      
      const response = await orderAPI.getOrder(orderId);
      
      if (response.success) {
        setOrder(response.data);
      } else {
        setError('Order not found');
      }
    } catch (err) {
      console.error('Error fetching order:', err);
      setError('Error loading order. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleCancelOrder = async () => {
    if (!window.confirm('Are you sure you want to cancel this order?')) return;

    try {
      setCancelling(true);
      
      const response = await orderAPI.cancelOrder(orderId);
      
      if (response.success) {
        setOrder(response.data);
      } else {
        setError(response.message || 'Failed to cancel order');
      }
    } catch (err) {
      console.error('Error cancelling order:', err);
      setError('Error cancelling order. Please try again.');
    } finally {
      setCancelling(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'pending':
        return { bg: '#FAFAFA', text: '#2C2C2C', border: '#D3D3D3' };
      case 'confirmed':
        return { bg: '#F5F5F5', text: '#2C2C2C', border: '#D3D3D3' };
      case 'processing':
        return { bg: '#F5F5F5', text: '#2C2C2C', border: '#D3D3D3' };
      case 'shipped':
        return { bg: '#F5F5F5', text: '#2C2C2C', border: '#D3D3D3' };
      case 'delivered':
        return { bg: '#F5F5F5', text: '#2C2C2C', border: '#D3D3D3' };
      case 'cancelled':
        return { bg: '#F5F5F5', text: '#6B6B6B', border: '#D3D3D3' };
      default:
        return { bg: '#F5F5F5', text: '#2C2C2C', border: '#D3D3D3' };
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-IN', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getOrderProgress = () => {
    const steps = [
      { key: 'pending', label: 'Order Placed' },
      { key: 'confirmed', label: 'Confirmed' },
      { key: 'processing', label: 'Processing' },
      { key: 'shipped', label: 'Shipped' },
      { key: 'delivered', label: 'Delivered' }
    ];

    const statusOrder = ['pending', 'confirmed', 'processing', 'shipped', 'delivered'];
    const currentIndex = statusOrder.indexOf(order.status);

    return steps.map((step, index) => ({
      ...step,
      completed: index <= currentIndex && order.status !== 'cancelled',
      current: index === currentIndex && order.status !== 'cancelled'
    }));
  };

  if (loading) {
    return (
      <div style={{ minHeight: '100vh', background: '#F5F5F5', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div style={{ width: '8rem', height: '8rem', border: '4px solid #D3D3D3', borderTop: '4px solid #2C2C2C', borderRadius: '50%', animation: 'spin 1s linear infinite' }}></div>
      </div>
    );
  }

  if (error || !order) {
    const statusColor = getStatusColor('default');
    return (
      <div style={{ minHeight: '100vh', background: '#F5F5F5', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{ color: '#6B6B6B', fontSize: '1.125rem', marginBottom: '1rem' }}>{error || 'Order not found'}</div>
          <button
            onClick={() => navigate('/orders')}
            style={{
              padding: '0.5rem 1rem',
              background: 'linear-gradient(135deg, #2C2C2C 0%, #4A4A4A 50%, #6B6B6B 100%)',
              color: 'white',
              borderRadius: '0.375rem',
              border: 'none',
              cursor: 'pointer',
              fontSize: '0.875rem',
              fontWeight: '500'
            }}
            onMouseEnter={(e) => e.target.style.opacity = '0.9'}
            onMouseLeave={(e) => e.target.style.opacity = '1'}
          >
            Back to Orders
          </button>
        </div>
      </div>
    );
  }

  const statusColor = getStatusColor(order.status);

  return (
    <div style={{ minHeight: '100vh', background: '#F5F5F5' }}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Success Message */}
        {orderCreated && (
          <div style={{ background: '#F5F5F5', border: '1px solid #D3D3D3', borderRadius: '0.375rem', padding: '1rem', marginBottom: '1.5rem' }}>
            <div style={{ display: 'flex' }}>
              <div style={{ color: '#8B4513', fontSize: '1.25rem', marginRight: '0.75rem' }}>✓</div>
              <div>
                <h3 style={{ fontSize: '0.875rem', fontWeight: '500', color: '#2C2C2C', marginBottom: '0.25rem' }}>
                  Order placed successfully!
                </h3>
                <p style={{ marginTop: '0.25rem', fontSize: '0.875rem', color: '#6B6B6B' }}>
                  Your order has been received and is being processed.
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Header */}
        <div style={{ marginBottom: '2rem' }}>
          <button
            onClick={() => navigate('/orders')}
            style={{
              marginBottom: '1rem',
              display: 'flex',
              alignItems: 'center',
              color: '#6B6B6B',
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              fontSize: '0.875rem',
              fontWeight: '500'
            }}
            onMouseEnter={(e) => e.target.style.color = '#2C2C2C'}
            onMouseLeave={(e) => e.target.style.color = '#6B6B6B'}
          >
            <span style={{ marginRight: '0.5rem' }}>←</span>
            Back to Orders
          </button>
          
          <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
            <div>
              <h1 style={{ fontSize: '1.875rem', fontWeight: '700', color: '#2C2C2C', marginBottom: '0.25rem' }}>Order #{order.orderId}</h1>
              <p style={{ color: '#6B6B6B', marginTop: '0.25rem' }}>Placed on {formatDate(order.orderDate)}</p>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
              <span style={{
                display: 'inline-flex',
                alignItems: 'center',
                padding: '0.5rem 1rem',
                borderRadius: '9999px',
                fontSize: '0.875rem',
                fontWeight: '500',
                background: statusColor.bg,
                color: statusColor.text,
                border: `1px solid ${statusColor.border}`
              }}>
                {order.status.charAt(0).toUpperCase() + order.status.slice(1)}
              </span>
            </div>
          </div>
        </div>

        {error && (
          <div style={{ background: '#FAFAFA', border: '1px solid #D3D3D3', borderRadius: '0.375rem', padding: '1rem', marginBottom: '1.5rem' }}>
            <div style={{ color: '#6B6B6B' }}>{error}</div>
          </div>
        )}

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(12, minmax(0, 1fr))', gap: '2rem' }}>
          {/* Main Content */}
          <div style={{ gridColumn: 'span 8 / span 8', display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
            {/* Order Progress */}
            {order.status !== 'cancelled' && (
              <div style={{ background: '#FAFAFA', borderRadius: '0.5rem', boxShadow: '0 1px 2px 0 rgba(0, 0, 0, 0.05)', padding: '1.5rem', border: '1px solid #D3D3D3' }}>
                <h2 style={{ fontSize: '1.125rem', fontWeight: '500', color: '#2C2C2C', marginBottom: '1.5rem' }}>Order Progress</h2>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                  {getOrderProgress().map((step, index) => (
                    <div key={step.key} style={{ display: 'flex', alignItems: 'center' }}>
                      <div style={{
                        flexShrink: 0,
                        width: '2rem',
                        height: '2rem',
                        borderRadius: '50%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        background: step.completed 
                          ? 'linear-gradient(135deg, #2C2C2C 0%, #4A4A4A 50%, #6B6B6B 100%)'
                          : step.current 
                          ? '#F5F5F5'
                          : '#E5E5E5',
                        color: step.completed ? 'white' : step.current ? '#2C2C2C' : '#6B6B6B',
                        border: step.current ? '2px solid #8B4513' : 'none'
                      }}>
                        {step.completed ? (
                          <span style={{ fontSize: '0.875rem' }}>✓</span>
                        ) : (
                          <span style={{ fontSize: '0.875rem', fontWeight: '500' }}>{index + 1}</span>
                        )}
                      </div>
                      <div style={{ marginLeft: '1rem' }}>
                        <div style={{
                          fontSize: '0.875rem',
                          fontWeight: '500',
                          color: step.completed || step.current ? '#2C2C2C' : '#6B6B6B'
                        }}>
                          {step.label}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Order Items */}
            <div style={{ background: '#FAFAFA', borderRadius: '0.5rem', boxShadow: '0 1px 2px 0 rgba(0, 0, 0, 0.05)', border: '1px solid #D3D3D3' }}>
              <div style={{ padding: '1.5rem', borderBottom: '1px solid #E5E5E5' }}>
                <h2 style={{ fontSize: '1.125rem', fontWeight: '500', color: '#2C2C2C' }}>Order Items</h2>
              </div>
              <div>
                {order.items.map((item, index) => (
                  <div key={index} style={{ padding: '1.5rem', borderBottom: index < order.items.length - 1 ? '1px solid #E5E5E5' : 'none' }}>
                    <div style={{ display: 'flex', alignItems: 'flex-start', gap: '1rem' }}>
                      <img
                        src={(() => {
                          // Check if it's an emergency service
                          const emergencyImageUrl = getEmergencyServiceImageUrl(item.name);
                          if (emergencyImageUrl) {
                            return emergencyImageUrl;
                          }
                          // Otherwise, use the product image
                          return item.productId?.image 
                            ? (item.productId.image.startsWith('http') 
                                ? item.productId.image 
                                : `https://weheal-backend.onrender.com${item.productId.image}`)
                            : '/placeholder-product.png';
                        })()}
                        alt={item.name}
                        style={{ height: '5rem', width: '5rem', borderRadius: '0.375rem', objectFit: 'cover', flexShrink: 0, border: '1px solid #D3D3D3' }}
                        onError={(e) => {
                          // Only fallback to placeholder if not an emergency service
                          const emergencyImageUrl = getEmergencyServiceImageUrl(item.name);
                          if (!emergencyImageUrl) {
                            e.target.src = '/placeholder-product.png';
                          }
                        }}
                      />
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <h3 style={{ fontSize: '1.125rem', fontWeight: '500', color: '#2C2C2C' }}>{item.name}</h3>
                        <p style={{ fontSize: '0.875rem', color: '#6B6B6B', marginTop: '0.25rem' }}>
                          {item.category === 'medicine' ? '💊 Medicine' : '🧪 Lab Test'}
                        </p>
                        <div style={{ marginTop: '0.5rem', display: 'flex', alignItems: 'center', gap: '1rem', fontSize: '0.875rem', color: '#6B6B6B' }}>
                          <span>Quantity: {item.quantity}</span>
                          <span>Price: ৳{item.price} each</span>
                        </div>
                      </div>
                      <div style={{ textAlign: 'right' }}>
                        <div style={{ fontSize: '1.125rem', fontWeight: '500', color: '#2C2C2C' }}>
                          ৳{item.total.toFixed(2)}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Sidebar */}
          <div style={{ gridColumn: 'span 4 / span 4', marginTop: '2rem', display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
            {/* Order Summary */}
            <div style={{ background: '#FAFAFA', borderRadius: '0.5rem', boxShadow: '0 1px 2px 0 rgba(0, 0, 0, 0.05)', padding: '1.5rem', border: '1px solid #D3D3D3' }}>
              <h2 style={{ fontSize: '1.125rem', fontWeight: '500', color: '#2C2C2C', marginBottom: '1rem' }}>Order Summary</h2>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.875rem' }}>
                  <span style={{ color: '#6B6B6B' }}>Subtotal</span>
                  <span style={{ color: '#2C2C2C' }}>৳{order.totalAmount.toFixed(2)}</span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.875rem' }}>
                  <span style={{ color: '#6B6B6B' }}>Shipping</span>
                  <span style={{ color: '#2C2C2C' }}>Free</span>
                </div>
                <div style={{ borderTop: '1px solid #E5E5E5', paddingTop: '0.75rem' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '1.125rem', fontWeight: '500' }}>
                    <span style={{ color: '#2C2C2C' }}>Total</span>
                    <span style={{ color: '#2C2C2C' }}>৳{order.totalAmount.toFixed(2)}</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Shipping Address */}
            <div style={{ background: '#FAFAFA', borderRadius: '0.5rem', boxShadow: '0 1px 2px 0 rgba(0, 0, 0, 0.05)', padding: '1.5rem', border: '1px solid #D3D3D3' }}>
              <h2 style={{ fontSize: '1.125rem', fontWeight: '500', color: '#2C2C2C', marginBottom: '1rem' }}>Shipping Address</h2>
              <div style={{ fontSize: '0.875rem', color: '#6B6B6B', display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
                <div>{order.shippingAddress.street}</div>
                <div>{order.shippingAddress.city}, {order.shippingAddress.state}</div>
                <div>{order.shippingAddress.zipCode}</div>
                <div>{order.shippingAddress.country}</div>
              </div>
            </div>

            {/* Tracking Info */}
            {order.trackingNumber && (
              <div style={{ background: '#FAFAFA', borderRadius: '0.5rem', boxShadow: '0 1px 2px 0 rgba(0, 0, 0, 0.05)', padding: '1.5rem', border: '1px solid #D3D3D3' }}>
                <h2 style={{ fontSize: '1.125rem', fontWeight: '500', color: '#2C2C2C', marginBottom: '1rem' }}>Tracking Information</h2>
                <div style={{ fontSize: '0.875rem' }}>
                  <div style={{ color: '#6B6B6B' }}>Tracking Number</div>
                  <div style={{ fontWeight: '500', color: '#2C2C2C' }}>{order.trackingNumber}</div>
                </div>
              </div>
            )}

            {/* Estimated Delivery */}
            {order.estimatedDelivery && order.status !== 'delivered' && order.status !== 'cancelled' && (
              <div style={{ background: '#FAFAFA', borderRadius: '0.5rem', boxShadow: '0 1px 2px 0 rgba(0, 0, 0, 0.05)', padding: '1.5rem', border: '1px solid #D3D3D3' }}>
                <h2 style={{ fontSize: '1.125rem', fontWeight: '500', color: '#2C2C2C', marginBottom: '1rem' }}>Estimated Delivery</h2>
                <div style={{ fontSize: '0.875rem' }}>
                  <div style={{ fontWeight: '500', color: '#2C2C2C' }}>
                    {formatDate(order.estimatedDelivery)}
                  </div>
                </div>
              </div>
            )}

            {/* Order Notes */}
            {order.notes && (
              <div style={{ background: '#FAFAFA', borderRadius: '0.5rem', boxShadow: '0 1px 2px 0 rgba(0, 0, 0, 0.05)', padding: '1.5rem', border: '1px solid #D3D3D3' }}>
                <h2 style={{ fontSize: '1.125rem', fontWeight: '500', color: '#2C2C2C', marginBottom: '1rem' }}>Order Notes</h2>
                <div style={{ fontSize: '0.875rem', color: '#6B6B6B' }}>
                  {order.notes}
                </div>
              </div>
            )}

            {/* Actions */}
            {['pending', 'confirmed'].includes(order.status) && (
              <div style={{ background: '#FAFAFA', borderRadius: '0.5rem', boxShadow: '0 1px 2px 0 rgba(0, 0, 0, 0.05)', padding: '1.5rem', border: '1px solid #D3D3D3' }}>
                <button
                  onClick={handleCancelOrder}
                  disabled={cancelling}
                  style={{
                    width: '100%',
                    padding: '0.5rem 1rem',
                    fontSize: '0.875rem',
                    fontWeight: '500',
                    color: '#6B6B6B',
                    background: '#F5F5F5',
                    border: '1px solid #D3D3D3',
                    borderRadius: '0.375rem',
                    cursor: cancelling ? 'not-allowed' : 'pointer',
                    opacity: cancelling ? 0.5 : 1
                  }}
                  onMouseEnter={(e) => {
                    if (!cancelling) {
                      e.target.style.background = '#EEEEEE';
                      e.target.style.color = '#2C2C2C';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (!cancelling) {
                      e.target.style.background = '#F5F5F5';
                      e.target.style.color = '#6B6B6B';
                    }
                  }}
                >
                  {cancelling ? 'Cancelling...' : 'Cancel Order'}
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
      <style>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
};

export default OrderDetailPage;
